import { useMutation, useQuery } from '@apollo/client';
import { InfoOutlined as IconInfoOutlined } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import { GetMultifactorAuthStatus, MfaType, SwitchMfaActivation } from 'Apollo';
import { PaperLoading } from 'Components/Shared';
import { PersonContext } from 'Context/CurrentPerson';
import useNotification from 'Hooks/useNotification';
import useTranslate from 'Hooks/useTranslate';
import { TProfileManagerMessagesKeys } from 'Languages/TProfileManagerMessages';
import { useContext, useEffect, useRef } from 'react';
import { generatePath } from 'react-router-dom';
import { PrivateRoutes } from 'Router/Routes';
import { PaperTitle } from 'Theme/components';
import joinURL from 'url-join';
import { MfaReadyDisplay } from './MfaReadyDisplay';
import MfaUnavailableOrInitialize from './MfaUnavailableOrInitialize';

interface MfaActionProps {
  readonly canUseMfa: boolean;
  readonly isInitialized: boolean;
  readonly isCurrentUser: boolean;
  readonly userImmutableId: string;
  readonly userEmail: string;
  readonly isMfaActive: boolean;
  readonly type: MfaType;
  readonly isForcedByOrganization: boolean;
  readonly isBlockedByOrganization: boolean;
  readonly canTransfertToEmail: boolean;
  readonly canDelete: boolean;
  readonly refreshUserProfile: () => Promise<void>;
}

function MfaAction({
  canUseMfa,
  isInitialized,
  isCurrentUser,
  userImmutableId,
  userEmail,
  isMfaActive,
  type,
  isForcedByOrganization,
  isBlockedByOrganization,
  canTransfertToEmail,
  canDelete,
  refreshUserProfile
}: MfaActionProps) {
  if (isInitialized) {
    //Cheking this condition first for an admin to know if it's user had configured TOTP
    return (
      <MfaReadyDisplay
        isCurrentUser={isCurrentUser}
        userImmutableId={userImmutableId}
        userEmail={userEmail}
        isMfaActive={isMfaActive}
        type={type}
        isForcedByOrganization={isForcedByOrganization}
        canTransfertToEmail={canTransfertToEmail}
        canDelete={canDelete}
        refreshUserProfile={refreshUserProfile}
      />
    );
  }

  let mfaInitializeLabelKey = `page.userProfile.security.mfa.${type.toLowerCase()}.initialize` as TProfileManagerMessagesKeys;
  return (
    <MfaUnavailableOrInitialize
      canUseMfa={canUseMfa}
      isCurrentUser={isCurrentUser}
      initializeLabel={mfaInitializeLabelKey}
      loginType={`initialize${type.toLowerCase()}mfa`}
      isBlockedByOrganization={isBlockedByOrganization}
    />
  );
}

interface CircleProps {
  readonly color?: string;
}

function Circle({ color }: CircleProps) {
  return (
    <Box
      sx={{
        bgcolor: color,
        width: 15,
        height: 15,
        marginRight: '-5px',
        marginLeft: '7px',
        borderRadius: '50%'
      }}
    />
  );
}

interface ActivationControlProps {
  readonly isMfaActive: boolean;
  readonly personImmutableId: string;
  readonly isMfaForcedByOrganization: boolean;
}

function ActivationControl({
  isMfaActive,
  personImmutableId,
  isMfaForcedByOrganization
}: ActivationControlProps) {
  const ts = useTranslate();
  const theme = useTheme();
  const { currentPerson } = useContext(PersonContext);

  const handleToggle = () => {
    const redirectUri =
      generatePath(PrivateRoutes.usersProfile.path, {
        accountCode: currentPerson.account ? currentPerson.account.code : 'all',
        immutableId: personImmutableId
      }) + '/security/?switchMfa=true';

    const challengeUrl = `${AppSettings.API_ENDPOINT_URI
      }api/mfa/challenge?redirectUri=${encodeURIComponent(
        joinURL('/', redirectUri)
      )}`;
    globalThis.location.href = challengeUrl;
  };

  const forcedMfaEnableTootTip = isMfaActive
    ? ts('page.userProfile.security.mfa.disableNotAllowed')
    : ts('page.userProfile.security.mfa.enableNotAllowed');

  return (
    <Box display="flex" alignItems="center" gap={2}>
      <Chip
        label={
          isMfaActive
            ? ts('common.status.activated')
            : ts('common.status.disabled')
        }
        icon={
          <Circle
            color={
              isMfaActive ? theme.palette.primary.main : theme.palette.grey[500]
            }
          />
        }
      />
      <Tooltip title={isMfaForcedByOrganization ? forcedMfaEnableTootTip : ''}>
        <span>
          <Button
            variant="contained"
            onClick={handleToggle}
            color={isMfaActive ? 'error' : 'primary'}
            disabled={isMfaForcedByOrganization}
          >
            {isMfaActive
              ? ts('common.action.deactivate')
              : ts('common.action.activate')}
          </Button>
        </span>
      </Tooltip>
    </Box>
  );
}

interface Props {
  readonly person: IPersonDetailed;
}

function TabSecurity({ person }: Props) {
  const ts = useTranslate();

  const { onError, onSuccess } = useNotification();
  const { currentPerson, currentPersonRoles } = useContext(PersonContext);
  const { data, loading, refetch } = useQuery<GetMultifactorAuthStatusData>(
    GetMultifactorAuthStatus,
    {
      onError,
      variables: { immutableId: person.immutableId }
    }
  );

  const [switchPersonMfa] = useMutation<
    SwitchMfaActivationData,
    SwitchMfaActivationVariables
  >(SwitchMfaActivation, { onError });

  const executedRef = useRef(false);
  const isCurrentUser = currentPerson.immutableId === person.immutableId;

  const canUseMfa = !!data?.multifactorAuthStatus?.canUseMultifactor;
  const isTotpInitialized = !!data?.multifactorAuthStatus?.totpInitialized;
  const isEmailMfaInitialized = !!data?.multifactorAuthStatus?.emailMfaInitialized;
  const isMfaActive = !!data?.multifactorAuthStatus?.isActive;
  const allowedToUpdateOwnTotp2fa = !!data?.multifactorAuthStatus?.allowedToUpdateTotp2fa;
  const allowedToUpdateEmail2fa = !!data?.multifactorAuthStatus?.allowedToUpdateEmail2fa;

  const isOnlyOrgOwner = currentPersonRoles.isOrganizationOwner && !currentPersonRoles.canWriteAll && !currentPersonRoles.canReadAll;

  const mfaStates = [
    {
      type: MfaType.Totp,
      isInitialized: isTotpInitialized,
      tooltip: ts('page.userProfile.security.mfa.totp.tooltip'),
      title: ts('page.userProfile.security.mfa.totp.title'),
      isRecommended: true,
      isForcedByOrganization: !allowedToUpdateOwnTotp2fa,
      other2faIsForced: !allowedToUpdateEmail2fa,
      canTransfertToEmail:
        person.organization === null &&
        isTotpInitialized &&
        !isEmailMfaInitialized &&
        currentPersonRoles.canWriteAll,
      canDelete: isCurrentUser || isOnlyOrgOwner
    },
    {
      type: MfaType.Email,
      isInitialized: isEmailMfaInitialized,
      tooltip: ts('page.userProfile.security.mfa.email.tooltip'),
      title: ts('page.userProfile.security.mfa.email.title'),
      isRecommended: false,
      isForcedByOrganization: !allowedToUpdateEmail2fa,
      other2faIsForced: !allowedToUpdateOwnTotp2fa,
      canTransfertToEmail: false,
      canDelete: isCurrentUser || isOnlyOrgOwner
    }
  ];

  const canToggleMfa =
    !loading && mfaStates.some(state => state.isInitialized) && isCurrentUser;

  useEffect(() => {
    const params = new URLSearchParams(globalThis.location.search);
    const switchMfa = params.get('switchMfa');

    if (switchMfa === 'true' && !executedRef.current) {
      executedRef.current = true; // prevent double execution of the mutation

      (async () => {
        try {
          await switchPersonMfa({
            variables: { immutableId: person.immutableId }
          });
          const { data: refreshedData } = await refetch();
          const newStatus = refreshedData?.multifactorAuthStatus?.isActive;
          onSuccess(
            newStatus
              ? 'page.userProfile.security.mfa.activated'
              : 'page.userProfile.security.mfa.deactivated'
          );
        } finally {
          // Clean up URL
          params.delete('switchMfa');
          const newUrl = params.toString()
            ? `${globalThis.location.pathname}?${params.toString()}`
            : globalThis.location.pathname;
          globalThis.history.replaceState({}, '', newUrl);
        }
      })();
    }
  }, [
    person.immutableId,
    switchPersonMfa,
    refetch,
    onSuccess,
    ts,
    isMfaActive
  ]);

  return (
    <Paper>
      <PaperTitle
        title={ts('page.userProfile.security.mfa.title')}
        secondaryAction={
          canToggleMfa && (
            <ActivationControl
              isMfaActive={isMfaActive}
              personImmutableId={person.immutableId}
              isMfaForcedByOrganization={
                !allowedToUpdateOwnTotp2fa || !allowedToUpdateEmail2fa
              }
            />
          )
        }
      />
      <PaperLoading loading={loading}>
        <Grid container direction="column" rowSpacing={0}>
          {mfaStates.map(state => (
            <Grid item key={state.type}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="start"
                paddingX={2}
                paddingY={1}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  sx={{ padding: '10px' }}
                  gap={1}
                >
                  <Typography variant="body1">
                    {state.isRecommended
                      ? `${state.title} (${ts('page.userProfile.security.mfa.recommended')})`
                      : state.title}
                  </Typography>
                  <Tooltip title={state.tooltip}>
                    <IconInfoOutlined
                      fontSize="small"
                      sx={{ cursor: 'help' }}
                    />
                  </Tooltip>
                </Box>
                <MfaAction
                  canUseMfa={canUseMfa}
                  isInitialized={state.isInitialized}
                  isCurrentUser={isCurrentUser}
                  userImmutableId={person.immutableId}
                  userEmail={person.email!}
                  isMfaActive={isMfaActive}
                  type={state.type}
                  isForcedByOrganization={state.isForcedByOrganization}
                  isBlockedByOrganization={
                    state.other2faIsForced && !state.isForcedByOrganization
                  }
                  canTransfertToEmail={state.canTransfertToEmail}
                  canDelete={state.canDelete}
                  refreshUserProfile={async () => {
                    await refetch();
                  }}
                />
              </Box>
            </Grid>
          ))}
        </Grid>
      </PaperLoading>
    </Paper>
  );
}

export default TabSecurity;
